import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Paper, 
  CircularProgress, 
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  styled
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { uploadImage } from '../utils/api';
import { fetchProfiles as obtenerPerfiles } from '../utils/api';

const UploadContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: '12px',
}));

const FileInput = styled('input')({
  display: 'none',
});

const UploadButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2, 4),
  fontSize: '1.1rem',
}));

const PreviewContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  justifyContent: 'center',
}));

const PreviewImage = styled('img')(({ theme }) => ({
  height: '120px',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
}));

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const Upload = () => {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();
  const query = useQuery();
  
  // Obtener el perfil de la URL si existe
  useEffect(() => {
    const profileId = query.get('profile');
    if (profileId) {
      setSelectedProfile(profileId);
    }
  }, [query.get('profile')]); // Solo depende del parametro 'profile' en la URL

  // Cargar perfiles solo una vez al montar el componente
  useEffect(() => {
    const cargarPerfiles = async () => {
      try {
        // Via la API, no leyendo Supabase directo desde el navegador: asi las
        // portadas vienen con la url reescrita al bucket actual, en lugar de
        // apuntar al dominio viejo que ya no resuelve.
        const perfiles = await obtenerPerfiles();
        setProfiles(perfiles);
      } catch (err) {
        console.error('Error fetching profiles:', err);
      }
    };

    cargarPerfiles();
  }, []);  // Dependencia vacía para que solo se ejecute una vez al montar el componente
  
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      setFiles(selectedFiles);
      
      // Create previews
      const newPreviews = [];
      selectedFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews.push(reader.result);
          if (newPreviews.length === selectedFiles.length) {
            setPreviews(newPreviews);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (files.length === 0) {
      setError('Please select at least one file to upload');
      return;
    }
    
    if (!user) {
      setError('You must be logged in to upload images');
      return;
    }
    
    if (!selectedProfile) {
      setError('Please select a collection');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setUploadProgress(0);
      
      // Cada imagen se sube por separado: primero el binario por streaming y
      // despues sus metadatos. Ya no hay lotes de 20 con multipart, porque eso
      // obligaba al servidor a cargar los 20 archivos en memoria a la vez.
      //
      // Se manda de a CONCURRENCIA en paralelo para no abrir 200 conexiones si
      // alguien selecciona una carpeta entera.
      const CONCURRENCIA = 4;
      const totalFiles = files.length;
      let terminados = 0;
      let successfulUploads = 0;

      const cola = [...files];

      const trabajador = async () => {
        while (cola.length > 0) {
          const file = cola.shift();

          try {
            await uploadImage(file, selectedProfile);
            successfulUploads++;
          } catch (fileError) {
            console.error(`Error subiendo ${file.name}:`, fileError);
            // Seguimos con el resto: que una falle no cancela la tanda.
          } finally {
            terminados++;
            setUploadProgress(Math.min(Math.round((terminados / totalFiles) * 100), 99));
          }
        }
      };

      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCIA, totalFiles) }, trabajador),
      );

      // Verificar resultado final
      if (successfulUploads > 0) {
        setSuccess(true);
        setUploadProgress(100);
        console.log(`Subida completada: ${successfulUploads} de ${totalFiles} imágenes`);
      } else {
        throw new Error(`No se pudo subir ninguna imagen de ${totalFiles}`);
      }
      
      // Limpiar el formulario
      setFiles([]);
      setPreviews([]);
      
      // Redirigir al perfil
      setTimeout(() => {
        navigate(`/profile/${selectedProfile}`);
      }, 2000);
    } catch (error) {
      console.error('Error in upload process:', error);
      setError(error.response?.data?.error || 'Failed to upload images');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container maxWidth="md" sx={{ pt: 10, pb: 6 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Upload Images
      </Typography>
      
      <UploadContainer elevation={3}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Images uploaded successfully!
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="profile-select-label">Collection</InputLabel>
            <Select
              labelId="profile-select-label"
              id="profile-select"
              name="profile"
              // El id de la coleccion llega por la URL, pero las opciones se
              // piden despues: en ese render el valor no coincide con ninguna y
              // MUI avisa. Tambien cubre el caso de un id que ya no existe.
              value={
                profiles.some((p) => p.id === selectedProfile) ? selectedProfile : ''
              }
              onChange={(e) => setSelectedProfile(e.target.value)}
              label="Collection"
              required
            >
              {profiles.map((profile) => (
                <MenuItem key={profile.id} value={profile.id}>
                  {profile.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Divider sx={{ mb: 3 }} />
          
          <Box sx={{ textAlign: 'center' }}>
            <label htmlFor="upload-images">
              <FileInput
                accept="image/*"
                id="upload-images"
                type="file"
                multiple
                onChange={handleFileChange}
              />
              <UploadButton
                variant="contained"
                component="span"
                startIcon={<CloudUploadIcon />}
                size="large"
                fullWidth
              >
                Select Images
              </UploadButton>
            </label>
          </Box>
          
          {previews.length > 0 && (
            <PreviewContainer>
              {previews.map((preview, index) => (
                <PreviewImage key={index} src={preview} alt={`Preview ${index + 1}`} />
              ))}
            </PreviewContainer>
          )}
          
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || files.length === 0 || !selectedProfile}
              size="large"
              fullWidth
            >
              {loading ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  Uploading... {uploadProgress}%
                </>
              ) : (
                `Upload ${files.length > 0 ? files.length : ''} Image${files.length !== 1 ? 's' : ''}`
              )}
            </Button>
          </Box>
        </form>
      </UploadContainer>
    </Container>
  );
};

export default Upload;
